import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Stack,
  Divider,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  DeleteForever as DeleteForeverIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  getAllEnvVariables,
  downloadCSV,
  importFromCSV,
  parseCSVFile,
  clearAllEnvVariables,
  saveEnvVariable,
  deleteEnvVariable,
  getDotEnvProfile,
  saveDotEnvProfile,
  getSavedFilePath,
  selectFileLocation,
  autoLoadFromFile,
  isAutoSaveAvailable,
  clearFilePath,
  clearFileHandle,
} from '../services/envVariableService';
import { EnvVariable } from '../types/envVariables';
import { envVariableIdentityKey, normalizeEnvVariableScope } from '../utils/executionEnvironment';
import { validateEnvironmentBaseUrl } from '../utils/environmentBaseUrlValidation';
import EnvVariableTable from '../components/EnvVariableTable';
import EnvVariableDialog from '../components/EnvVariableDialog';
import { useProjects } from '../context/ProjectContext';
import { PROJECT_SELECT_CONTROL_WIDTH } from './Projects';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';

const ENV_VARS_CARD_RADIUS = '14px';
const envVarsPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiCard-root': {
    borderRadius: `${ENV_VARS_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
} as const;
const ENV_VARS_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'visible',
  p: { xs: 1.25, sm: 1.5 },
} as const;
const envVarsCardSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative' as const,
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${ENV_VARS_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;
const envVarsDialogPaperSx = {
  width: { xs: 'calc(100% - 32px)', sm: 440 },
  maxWidth: 'min(92vw, 520px)',
  m: 2,
  borderRadius: '14px',
  border: `1px solid ${DFT_BRAND.line}`,
} as const;
const envVarsBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 36,
  minHeight: 36,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
};

const EnvVariables: React.FC = () => {
  const { selectedProject, projects, setSelectedProject, isLoading: projectsLoading } = useProjects();
  const [variables, setVariables] = useState<EnvVariable[]>([]);
  const [baseUrl, setBaseUrl] = useState('');
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);
  const [savingBaseUrl, setSavingBaseUrl] = useState(false);
  const persistedBaseUrlRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState<EnvVariable | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [variableToDelete, setVariableToDelete] = useState<EnvVariable | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importReplaceMode, setImportReplaceMode] = useState(false);
  const [filePath, setFilePath] = useState<string | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // Viewport-anchored Menu (not MUI Select): avoids first-paint misalignment under tenant CSS zoom on server.
  const projectAnchorRef = useRef<HTMLDivElement>(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [projectMenuPos, setProjectMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [projectMenuWidth, setProjectMenuWidth] = useState(0);

  const measureProjectMenu = () => {
    const el = projectAnchorRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      width: Math.round(rect.width),
      top: Math.round(rect.bottom + 4),
      left: Math.round(rect.left),
    };
  };

  const openProjectMenu = () => {
    const measured = measureProjectMenu();
    if (!measured) return;
    flushSync(() => {
      setProjectMenuWidth(measured.width);
      setProjectMenuPos({ top: measured.top, left: measured.left });
      setProjectMenuOpen(true);
    });
  };

  const closeProjectMenu = () => setProjectMenuOpen(false);

  const remeasureProjectMenu = () => {
    const measured = measureProjectMenu();
    if (!measured) return;
    setProjectMenuWidth(measured.width);
    setProjectMenuPos({ top: measured.top, left: measured.left });
  };

  // Load variables on mount from IndexedDB only; CSV is never read automatically
  useEffect(() => {
    const initializeVariables = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const isAvailable = isAutoSaveAvailable();
        setAutoSaveEnabled(isAvailable);
        
        const savedPath = await getSavedFilePath();
        setFilePath(savedPath);
        
        // Only load from IndexedDB; CSV is touched only by Export / Import / Reload
        // Variable loading is handled by the selectedProject effect.
      } catch (err: any) {
        setError(`Failed to load variables: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    initializeVariables();
  }, []);

  // When the selected project changes in the Environment (.Env) module,
  // refresh the in-memory variables so the view always reflects the
  // active project context and never shows stale data.
  useEffect(() => {
    if (!selectedProject) {
      setVariables([]);
      setBaseUrl('');
      persistedBaseUrlRef.current = '';
      setBaseUrlError(null);
      return;
    }
    // Prevent stale UI: clear immediately, then reload.
    setVariables([]);
    setEditingVariable(null);
    setAddDialogOpen(false);
    setSuccess(null);
    setError(null);

    // Re-read from IndexedDB (scoped per project).
    void loadVariables(selectedProject.id);
  }, [selectedProject?.id]);

  const loadVariables = async (projectId: number) => {
    try {
      const [vars, profile] = await Promise.all([
        getAllEnvVariables(projectId),
        getDotEnvProfile(projectId),
      ]);
      const validVars = vars.filter(v => 
        v.key && 
        v.key !== 'env-variables-file-handle' && 
        v.value !== undefined && 
        v.value !== null
      );
      setVariables(validVars);
      const loadedBaseUrl = profile?.baseUrl ?? '';
      setBaseUrl(loadedBaseUrl);
      persistedBaseUrlRef.current = loadedBaseUrl;
      setBaseUrlError(null);
    } catch (err: any) {
      console.error('Failed to load variables:', err);
    }
  };

  const handleBaseUrlChange = (value: string) => {
    setBaseUrl(value);
    if (baseUrlError) {
      setBaseUrlError(validateEnvironmentBaseUrl(value));
    }
  };

  const persistBaseUrlIfValid = async (): Promise<boolean> => {
    if (!selectedProject) return false;
    const trimmed = baseUrl.trim();
    const urlError = validateEnvironmentBaseUrl(trimmed);
    if (urlError) {
      setBaseUrlError(urlError);
      return false;
    }
    if (trimmed === persistedBaseUrlRef.current) {
      setBaseUrlError(null);
      return true;
    }
    try {
      setSavingBaseUrl(true);
      await saveDotEnvProfile(selectedProject.id, trimmed);
      persistedBaseUrlRef.current = trimmed;
      setBaseUrl(trimmed);
      setBaseUrlError(null);
      return true;
    } catch (err: any) {
      setBaseUrlError(err.message || 'Failed to save base URL');
      return false;
    } finally {
      setSavingBaseUrl(false);
    }
  };

  const handleBaseUrlBlur = async () => {
    await persistBaseUrlIfValid();
  };

  const requireValidBaseUrl = async (): Promise<boolean> => {
    return persistBaseUrlIfValid();
  };

  const handleAdd = async () => {
    if (!(await requireValidBaseUrl())) {
      setError('Set a valid Base URL before adding variables');
      return;
    }
    setEditingVariable(null);
    setAddDialogOpen(true);
  };

  const handleEdit = (variable: EnvVariable) => {
    setEditingVariable(variable);
    setAddDialogOpen(true);
  };

  const handleSave = async (variable: EnvVariable, options?: { previousKey?: string; previousScope?: EnvVariable['scope'] }) => {
    try {
      if (!selectedProject) {
        setError('No project selected');
        return;
      }
      if (!(await requireValidBaseUrl())) {
        setError('Set a valid Base URL before saving variables');
        return;
      }
      // Validate that key and value are not empty
      if (!variable.key || !variable.value) {
        setError('Key and value are required');
        return;
      }

      // If key changed during edit, delete the old variable first
      if (
        options?.previousKey &&
        (options.previousKey !== variable.key ||
          normalizeEnvVariableScope(options.previousScope) !== normalizeEnvVariableScope(variable.scope))
      ) {
        await deleteEnvVariable(selectedProject.id, options.previousKey, options.previousScope);
      }
      
      await saveEnvVariable(selectedProject.id, variable);
setSuccess(editingVariable ? 'Variable updated successfully' : 'Variable added successfully');
setAddDialogOpen(false);
setEditingVariable(null);
      await loadVariables(selectedProject.id);
      
      // Auto-save to file if path is set
      // if (filePath && autoSaveEnabled) {
      //   const result = await autoSaveToFile();
      //   if (result.success) {
      //     setSuccess((editingVariable ? 'Variable updated' : 'Variable added') + ' and auto-saved to file');
      //   } else {
      //     setError(`Variable saved to browser, but auto-save to file failed: ${result.error}`);
      //     console.error('Auto-save failed:', result.error);
      //   }
      // }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(`Failed to save variable: ${err.message}`);
    }
  };

  const handleDeleteClick = (variable: EnvVariable) => {
    setVariableToDelete(variable);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!variableToDelete) return;
    if (!selectedProject) return;
    try {
      await deleteEnvVariable(selectedProject.id, variableToDelete.key, variableToDelete.scope);
      setDeleteConfirmOpen(false);
      setVariableToDelete(null);
      await loadVariables(selectedProject.id);
      setSuccess('Variable deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(`Failed to delete variable: ${err.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setVariableToDelete(null);
  };

  const handleDelete = handleDeleteClick;

  const handleExport = async () => {
    try {
      if (!selectedProject) return;
      await downloadCSV('env-variables.csv', selectedProject.id);
      setSuccess('CSV file downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(`Failed to export: ${err.message}`);
    }
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const handleImport = (replaceExisting: boolean) => {
    setImportReplaceMode(replaceExisting);
    setImportDialogOpen(false);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - only accept CSV files
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv')) {
      setError('Only CSV files are allowed. Please select a .csv file.');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate MIME type if available
    if (file.type && !file.type.includes('csv') && !file.type.includes('text/csv') && !file.type.includes('text/plain')) {
      // Some browsers may not set MIME type correctly, so we'll be lenient
      // But if it's explicitly set to something non-CSV, reject it
      if (file.type !== 'application/vnd.ms-excel' && !file.type.includes('text')) {
        setError('Invalid file type. Only CSV files are allowed.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      const content = await parseCSVFile(file);
      if (!selectedProject) throw new Error('No project selected');
      const result = await importFromCSV(content, importReplaceMode, selectedProject.id);
      
      if (result.errors.length > 0) {
        setError(`Imported ${result.success} variables with ${result.errors.length} errors:\n${result.errors.slice(0, 5).join('\n')}`);
      } else {
        const baseUrlNote = result.baseUrl ? ` (base URL: ${result.baseUrl})` : '';
        setSuccess(`Successfully imported ${result.success} variables${baseUrlNote}`);
        setTimeout(() => setSuccess(null), 3000);
      }
      
      if (!selectedProject) throw new Error('No project selected');
      await loadVariables(selectedProject.id);
    } catch (err: any) {
      setError(`Failed to import: ${err.message}`);
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // const handleClearAll = async () => {
  //   try {
  //     await clearAllEnvVariables();
  //     setSuccess('All variables cleared');
  //     setClearConfirmOpen(false);
  //     await loadVariables();
      
  //     // Auto-save to file if path is set
  //     if (filePath && autoSaveEnabled) {
  //       const result = await autoSaveToFile();
  //       if (!result.success) {
  //         console.warn('Auto-save failed:', result.error);
  //       }
  //     }
      
  //     setTimeout(() => setSuccess(null), 3000);
  //   } catch (err: any) {
  //     setError(`Failed to clear variables: ${err.message}`);
  //   }
  // };
  const handleClearAll = async () => {
    try {
      // Clear ONLY IndexedDB
      if (!selectedProject) return;
      await clearAllEnvVariables(selectedProject.id);
  
      setClearConfirmOpen(false);
      await loadVariables(selectedProject.id);

      setSuccess('All .Env data cleared from browser storage');
      setTimeout(() => setSuccess(null), 3000);
  
    } catch (err: any) {
      setError(`Failed to clear variables: ${err.message}`);
    }
  };
  

  const handleSelectFileLocation = () => {
    // Blur + defer past the click so closing the native picker cannot immediately re-fire this button
    // (Chromium/Edge: pointer event can land on the button under the dialog).
    (document.activeElement as HTMLElement | null)?.blur();
    window.setTimeout(async () => {
      try {
        console.log('[handleSelectFileLocation] Starting file selection...');
        const result = await selectFileLocation();
        console.log('[handleSelectFileLocation] Result:', result);
        if (result.success && result.path) {
          setFilePath(result.path);
          setSuccess(`Linked to: ${result.path}. Use "Reload from File" to load from it. The file is never modified automatically.`);
          setTimeout(() => setSuccess(null), 5000);
        } else if (result.error && result.error !== 'File selection cancelled') {
          setError(result.error);
        }
      } catch (err: any) {
        console.error('[handleSelectFileLocation] Error:', err);
        setError(`Failed to select file location: ${err.message}`);
      }
    }, 100);
  };

  const handleClearFileLocation = async () => {
    try {
      await clearFileHandle();
      await clearFilePath();
      setFilePath(null);
      setSuccess('File location cleared');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(`Failed to clear file location: ${err.message}`);
    }
  };

  const handleReloadFromFile = async () => {
    try {
      console.log('[handleReloadFromFile] Starting reload...');
      console.log('[handleReloadFromFile] Current filePath state:', filePath);
      setLoading(true);
      setError(null);
      
      if (!selectedProject) throw new Error('No project selected');
      const result = await autoLoadFromFile(selectedProject.id);
      console.log('[handleReloadFromFile] autoLoadFromFile result:', result);
      if (result.success) {
        await loadVariables(selectedProject.id);
        setSuccess(`Reloaded ${result.count} variables from file`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        console.error('[handleReloadFromFile] Failed with error:', result.error);
        // If the browser has no persisted file handle anymore (for example after
        // a browser change or storage reset) but the server still has a saved
        // path, clear the stale path so the user can link a new file.
        if (result.error === 'No file location set') {
          try {
            await clearFileHandle();
            await clearFilePath();
            setFilePath(null);
          } catch (cleanupError) {
            console.error('[handleReloadFromFile] Failed to clear stale file location:', cleanupError);
          }
          setError('No linked CSV file found. Please click "Set File Location" to link your CSV again.');
        } else {
          setError(`Failed to reload from file: ${result.error}`);
        }
      }
    } catch (err: any) {
      console.error('[handleReloadFromFile] Exception:', err);
      setError(`Failed to reload from file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={envVarsPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.25,
            pb: 0.5,
            minHeight: 36,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                m: 0,
                fontWeight: 700,
                height: 36,
                lineHeight: '36px',
                letterSpacing: '-0.02em',
                color: DFT_BRAND.text,
                whiteSpace: 'nowrap',
              }}
            >
              .Env Variables
            </Typography>
            <Chip
              icon={<WarningIcon />}
              label="Local Storage Only"
              color="warning"
              size="small"
              sx={{ height: 24, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
            />
          </Box>
          <FormControl
            size="small"
            variant="outlined"
            sx={{
              width: { xs: 180, sm: 220, md: 240, lg: PROJECT_SELECT_CONTROL_WIDTH.sm },
              flexShrink: 0,
              overflow: 'visible',
              position: 'relative',
              '& .MuiInputLabel-root': {
                backgroundColor: DFT_BRAND.paper,
                px: 0.5,
                lineHeight: 1.25,
                zIndex: 1,
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              },
            }}
          >
            <InputLabel id="env-project-select-label" shrink>
              Project
            </InputLabel>
            <Box
              ref={projectAnchorRef}
              id="env-project-select"
              role="combobox"
              aria-labelledby="env-project-select-label"
              aria-expanded={projectMenuOpen}
              aria-haspopup="listbox"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                openProjectMenu();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  openProjectMenu();
                }
              }}
              sx={{
                width: '100%',
                height: 36,
                border: `1px solid ${projectMenuOpen ? '#1d4ed8' : DFT_BRAND.line}`,
                borderRadius: '10px',
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.5,
                cursor: 'pointer',
                boxSizing: 'border-box',
                bgcolor: DFT_BRAND.paper,
                '&:hover': {
                  borderColor: projectMenuOpen ? '#1d4ed8' : DFT_BRAND.text,
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedProject?.name ?? ''}
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 20, flexShrink: 0, color: 'action.active' }} />
            </Box>
            <Menu
              open={projectMenuOpen && projectMenuPos != null}
              onClose={closeProjectMenu}
              anchorReference="anchorPosition"
              anchorPosition={projectMenuPos ?? { top: 0, left: 0 }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableScrollLock
              TransitionProps={{
                onEntering: remeasureProjectMenu,
                onEntered: remeasureProjectMenu,
              }}
              sx={{ zIndex: (theme) => theme.zIndex.modal }}
              PaperProps={{
                sx: {
                  width: projectMenuWidth > 0 ? `${projectMenuWidth}px !important` : undefined,
                  minWidth: projectMenuWidth > 0 ? `${projectMenuWidth}px !important` : undefined,
                  maxWidth: projectMenuWidth > 0 ? `${projectMenuWidth}px !important` : undefined,
                  boxSizing: 'border-box',
                  zIndex: (theme) => theme.zIndex.modal,
                },
              }}
              MenuListProps={{
                sx: {
                  maxHeight: 40 * 6 + 16,
                  overflowY: 'auto',
                  py: 0.5,
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  '& .MuiMenuItem-root': {
                    height: 40,
                    lineHeight: '40px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
            >
              {projectsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} /> Loading...
                </MenuItem>
              ) : (
                projects.map((project) => (
                  <MenuItem
                    key={project.id}
                    selected={selectedProject?.id === project.id}
                    title={project.name}
                    onClick={() => {
                      setSelectedProject(project);
                      closeProjectMenu();
                    }}
                  >
                    {project.name}
                  </MenuItem>
                ))
              )}
              {projects.length === 0 && !projectsLoading && (
                <MenuItem disabled>No projects found</MenuItem>
              )}
            </Menu>
          </FormControl>
        </Box>
      </Box>

      <Box sx={ENV_VARS_WORKSPACE_SX}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1.5 }}>
          <strong>Local execution environment:</strong> Set a <strong>Base URL</strong> (required, like server Environments), then add variables.
          Data stays on this computer (not in the CRUD Expert database). Each row has a key, value, scope, and description.
          Credentials here are used only when you choose <strong> .Env (local)</strong> to run a collection or test plan — they are not mixed with server Environments.
          Reference variables with <code>{'{{KEY_NAME}}'}</code>. Base URL and variables can be exported/imported via CSV.
        </Alert>
        
        {!autoSaveEnabled && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1.5 }}>
            <strong>Linked file unavailable:</strong> Your browser doesn't support the File System Access API, so you can't link a CSV for Reload. 
            You can still use Export to CSV, Import from CSV, and all data is stored in browser (IndexedDB). 
            For linking a file, use Chrome, Edge, or another Chromium-based browser.
          </Alert>
        )}
        
        {autoSaveEnabled && filePath && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1.5 }}>
            <strong>Linked CSV file:</strong> This file is only read when you click "Reload from File". 
            The app never writes to it automatically. Add/edit/delete only update browser storage (IndexedDB). 
            Use Export to CSV to download a copy, or Import from CSV to load from a file.
          </Alert>
        )}

      <Paper elevation={0} sx={{ ...envVarsCardSx, p: 1.5, mb: 1.5 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            size="small"
            label="Base URL"
            placeholder="https://api.example.com"
            value={baseUrl}
            onChange={(e) => handleBaseUrlChange(e.target.value)}
            onBlur={() => void handleBaseUrlBlur()}
            error={!!baseUrlError}
            helperText={
              baseUrlError ||
              'Required. Saved to browser storage when you leave the field or add a variable.'
            }
            disabled={!selectedProject || savingBaseUrl}
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.875rem', height: 40 },
            }}
          />

          {/* File Path Display */}
          {autoSaveEnabled && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap flexWrap="wrap">
                <TextField
                  fullWidth
                  size="small"
                  label="Linked CSV file (for Reload only)"
                  value={filePath || 'No file linked — click "Set File Location" to choose a CSV to reload from'}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                  }}
                  helperText={filePath ? 'Read only when you click Reload from File. The app never writes to this file automatically.' : 'Only .csv files. Select a CSV to reload from with the Reload button. Not modified by the app.'}
                  sx={{ minWidth: 0, flex: '1 1 240px' }}
                />
                <Box sx={{ mt: { xs: 0, sm: 0.5 }, flexShrink: 0 }}>
                  {filePath ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={handleReloadFromFile}
                        sx={{ ...envVarsBtnSx, minWidth: 0 }}
                        title="Reload variables from the CSV file if you edited it externally"
                      >
                        Reload from File
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={handleClearFileLocation}
                        sx={{ ...envVarsBtnSx, minWidth: 0 }}
                      >
                        Clear Location
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSelectFileLocation}
                      sx={{ ...envVarsBtnSx, minWidth: 0, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
                      title="Choose a .csv file to link for Reload from File (CSV only)"
                    >
                      Set File Location
                    </Button>
                  )}
                </Box>
              </Stack>
            </Box>
          )}
          
          {/* Action Buttons */}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ ...envVarsBtnSx, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
            >
              Add Variable
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!selectedProject}
              title="Export base URL and environment variables to a CSV file"
              sx={envVarsBtnSx}
            >
              Export to CSV
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleImportClick}
              title="Import environment variables from a CSV file (only .csv files accepted)"
              sx={envVarsBtnSx}
            >
              Import from CSV
            </Button>
            
            <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setClearConfirmOpen(true)}
              disabled={!selectedProject || (variables.length === 0 && !baseUrl.trim())}
              sx={envVarsBtnSx}
            >
              Clear All
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 1.5 }}>
          {success}
        </Alert>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <EnvVariableTable
          variables={variables}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      )}

      <Card elevation={0} sx={{ ...envVarsCardSx, mt: 1.5 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DFT_BRAND.text, mb: 0.75 }}>
            CSV Format
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              overflowX: 'auto',
              m: 0,
              whiteSpace: 'pre',
            }}
          >
{`key,value,description,createdAt,updatedAt
USERNAME,john.doe@example.com,Login username,2026-01-15T10:00:00Z,2026-01-15T10:00:00Z
PASSWORD,mySecretPass123,Login password,2026-01-15T10:00:00Z,2026-01-15T10:00:00Z
API_KEY,sk_test_123456789,Stripe API Key,2026-01-15T10:00:00Z,2026-01-15T10:00:00Z`}
          </Typography>
          
          <Divider sx={{ my: 1.5, borderColor: DFT_BRAND.line }} />
          
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DFT_BRAND.text, mb: 0.75 }}>
            Usage in Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
            Reference variables in your request body, headers, or URL using double curly braces:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              bgcolor: DFT_BRAND.tint,
              p: 1,
              borderRadius: '10px',
              overflowX: 'auto',
              m: 0,
              whiteSpace: 'pre',
            }}
          >
{`{
  "username": "{{USERNAME}}",
  "password": "{{PASSWORD}}",
  "api_key": "{{API_KEY}}"
}`}
          </Typography>
        </CardContent>
      </Card>
      </Box>

      {/* Add/Edit Dialog */}
      <EnvVariableDialog
        open={addDialogOpen}
        variable={editingVariable}
        existingIdentities={variables.map((v) => envVariableIdentityKey(v.key, v.scope))}
        onClose={() => {
          setAddDialogOpen(false);
          setEditingVariable(null);
        }}
        onSave={handleSave}
      />

      {/* Delete Single Variable Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth={false}
        PaperProps={{ sx: envVarsDialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700, fontSize: '1.05rem' }}>
          Delete Environment Variable
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            Are you sure you want to delete this environment variable{variableToDelete ? ` "${variableToDelete.key}"` : ''}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button onClick={handleDeleteCancel} sx={envVarsBtnSx}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={envVarsBtnSx}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Confirmation Dialog */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: envVarsDialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700, fontSize: '1.05rem' }}>
          Clear All Variables?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            Are you sure you want to delete all {variables.length} environment variables?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button onClick={() => setClearConfirmOpen(false)} sx={envVarsBtnSx}>Cancel</Button>
          <Button onClick={handleClearAll} color="error" variant="contained" sx={envVarsBtnSx}>
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Mode Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: envVarsDialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700, fontSize: '1.05rem' }}>
          Import CSV File
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Note:</strong> Only CSV (.csv) files are accepted for import.
          </Alert>
          <DialogContentText sx={{ mb: 2, fontSize: '0.875rem' }}>
            How would you like to import the CSV file?
          </DialogContentText>
          <Stack spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => handleImport(false)}
              fullWidth
              sx={envVarsBtnSx}
            >
              Merge with Existing Variables
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => handleImport(true)}
              fullWidth
              sx={envVarsBtnSx}
            >
              Replace All Existing Variables
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setImportDialogOpen(false)} sx={envVarsBtnSx}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv,application/vnd.ms-excel"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </Box>
  );
};

export default EnvVariables;
